import { type SxProps, type Theme } from "@mui/material";

import { mergeSx } from "ui/merge-sx";
import { type TextFieldProps } from "ui/text-field/text-field.types";

/**
 * The padding, the type step and the icons at either end of the control come from the
 * `MuiOutlinedInput` theme entry (`theme/components/field-geometry.ts`), which every field shares. What is
 * left here is `TextField`'s own.
 */

/** `inputActions` are buttons, which tuck in to 4px from the border instead of the size's inset. */
const ACTIONS_PADDING_RIGHT = 0.5;

export const ACTION_ROW_SX: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
};

/**
 * `display: flex` is what lets the control fill a field that has been told to stretch — a `flex: 1`
 * on `.MuiOutlinedInput-root` needs a flex parent to act on. Without it the height chain stops here
 * and a full-height textarea collapses to its rows.
 */
export const CONTROL_SX: SxProps<Theme> = { flex: 1, display: "flex" };

export const getControlSx = (
  hasInputActions: boolean,
  inputSx: TextFieldProps["inputSx"],
  controlSx: TextFieldProps["controlSx"],
) =>
  mergeSx(
    // Under `adornedEnd`, so it carries the same two classes as the theme's inset and wins on order.
    hasInputActions ? { "&.MuiInputBase-adornedEnd": { paddingRight: ACTIONS_PADDING_RIGHT } } : undefined,
    // The repeated class keeps a call site level with the theme's three-class `multiline` reset, which
    // it then beats on order — a `TextArea` whose consumer pads the input itself would otherwise lose it.
    inputSx ? { "& .MuiOutlinedInput-input.MuiOutlinedInput-input": inputSx } : undefined,
    controlSx,
  );
